'use client'
import { Button } from '@/components/button'
import { Card, CardContent } from '@/components/card'
import { Fader } from '@/components/fader'
import { ApplicationSVG, LogoSVG, CompanySVG } from '@/components/logo'
import { loadingOverlayAtom } from '@/components/providers/atoms-provider'
import { Text } from '@/components/text'
import { SIGN_IN_PAGE } from '@/constants/pages'
import type { SlideShowItem } from '@/models/types/application/slide-show-item'
import clsx from 'clsx'
import { useSetAtom } from 'jotai'
import { LogInIcon } from 'lucide-react'
import { AuthError } from 'next-auth'
import { signIn } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import type { JSX } from 'react'
import { useEffect } from 'react'

type SignInClientPageProps = {
    providers: { id: string; name: string }[]
    errorMessage?: string
    backgroundSlides?: SlideShowItem[]
}

function SignInClientPage(props: SignInClientPageProps): JSX.Element {
    const { providers, errorMessage, backgroundSlides = [] } = props
    const setOverlay = useSetAtom(loadingOverlayAtom)
    const router = useRouter()
    const backgroundUrls = (() => {
        const slides: string[] = []
        for (const slide of backgroundSlides) {
            if (slide.url) {
                slides.push(slide.url)
            }
        }
        return slides
    })()

    useEffect(() => {
        return () => {
            setOverlay({ visible: false })
        }
    }, [setOverlay, backgroundSlides])

    return (
        <Fader className={clsx('flex h-full w-full items-center justify-center')} urls={backgroundUrls}>
            <div className="flex h-full flex-col justify-around">
                <Card className="bg-white dark:bg-neutral-800">
                    <CardContent size="zero" className="flex flex-col gap-16 px-8 py-8">
                        <div className="flex flex-col gap-8">
                            <div className="flex items-center justify-center gap-1">
                                <LogoSVG className="h-16" />
                                <div className="flex flex-col items-end gap-1">
                                    <CompanySVG className="h-8" />
                                    <ApplicationSVG className="h-6" />
                                </div>
                            </div>
                            <Text type="subtitle2" className="text-center font-medium">
                                Advanced Monitoring System
                            </Text>
                        </div>
                        {errorMessage ? (
                            <div className="text-danger-600 dark:text-danger-500 text-center text-sm font-medium">
                                {errorMessage}
                            </div>
                        ) : null}
                        {providers.map((provider) => (
                            <Button
                                key={provider.id}
                                className="w-full sm:w-auto"
                                onClick={() => {
                                    try {
                                        setOverlay({ message: 'Autenticazione in corso...', visible: true })
                                        signIn(provider.id, { callbackUrl: '/' })
                                    } catch (catchedError) {
                                        console.log(catchedError)
                                        if (catchedError instanceof AuthError) {
                                            router.push(`${SIGN_IN_PAGE}?error=${catchedError.type}`, { scroll: false })
                                        }
                                        throw catchedError
                                    }
                                }}
                            >
                                <LogInIcon className="size-4" />
                                Accedi con {provider.name}
                            </Button>
                        ))}
                    </CardContent>
                </Card>
            </div>
        </Fader>
    )
}

export default SignInClientPage
